/**
 * DeckController — per-deck playback, hot cues, loops, tap tempo, sync
 */

export class DeckController {
  constructor(id, engine, state) {
    this.id     = id       // 'A' | 'B'
    this.engine = engine
    this.state  = state

    // Playback position tracking
    this.audioBuffer       = null
    this._source           = null
    this.isPlaying         = false
    this.startOffset       = 0     // seconds into buffer at last start
    this.startContextTime  = 0     // audioContext.currentTime at last start
    this.playbackRate      = 1.0

    // DJ controls
    this.bpm      = 128
    this.cuePoint = 0
    this.hotCues  = [null, null, null, null]
    this.mixPoint = null  // optimal mix point marker

    this.loopIn     = null
    this.loopOut    = null
    this.loopActive = false

    this._tapTimes   = []
    this._isSynced   = false
    this.keyLock     = true   // preserve pitch when tempo changes (default ON)

    // Track database support
    this.currentTrackId = null  // SHA-256 ID for cue persistence
    this.currentTrackMetadata = null  // { fileName, fileSize, duration }
  }

  // ─── Getters ──────────────────────────────────────────────────────────────

  get currentTime() {
    if (!this.isPlaying) return this.startOffset
    const elapsed = (this.engine.ctx.currentTime - this.startContextTime) * this.playbackRate
    return Math.min(this.startOffset + elapsed, this.duration || Infinity)
  }

  get duration() {
    return this.audioBuffer ? this.audioBuffer.duration : 0
  }

  get remaining() {
    return Math.max(0, this.duration - this.currentTime)
  }

  // ─── Track Database Support ────────────────────────────────────────────────

  /**
   * Generate file-based unique ID for track cue persistence
   * Uses: SHA-256(filename + filesize + duration)
   * Fast, deterministic, survives file moves (if metadata unchanged)
   */
  async generateTrackId(fileName, fileSize, duration) {
    if (!fileName || !fileSize || duration === undefined) return null

    try {
      // Create unique string from file metadata
      const trackString = `${fileName}|${fileSize}|${duration.toFixed(2)}`
      const encoder = new TextEncoder()
      const data = encoder.encode(trackString)

      // Compute SHA-256 hash using Web Crypto API
      const hashBuffer = await crypto.subtle.digest('SHA-256', data)
      const hashArray = Array.from(new Uint8Array(hashBuffer))
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')

      this.currentTrackId = hashHex
      this.currentTrackMetadata = { fileName, fileSize, duration }
      return hashHex
    } catch (err) {
      console.warn('Failed to generate track ID:', err)
      return null
    }
  }

  /**
   * Get current cue data for persistence
   */
  getCueData() {
    return {
      fileName: this.currentTrackMetadata?.fileName || 'Unknown',
      fileSize: this.currentTrackMetadata?.fileSize || 0,
      duration: this.currentTrackMetadata?.duration || this.duration,
      mainCue: this.cuePoint,
      hotCues: [...this.hotCues],  // Copy array to avoid mutations
      dateAdded: new Date().toISOString(),
    }
  }

  /**
   * Restore cue data from persistence
   */
  restoreCueData(cueData) {
    if (!cueData) return false

    try {
      if (cueData.mainCue !== undefined && cueData.mainCue !== null) {
        this.cuePoint = cueData.mainCue
      }
      if (Array.isArray(cueData.hotCues)) {
        this.hotCues = [...cueData.hotCues]  // Copy to avoid mutations
      }
      return true
    } catch (err) {
      console.warn('Failed to restore cue data:', err)
      return false
    }
  }

  // ─── Transport ────────────────────────────────────────────────────────────

  play() {
    if (this.isPlaying || !this.audioBuffer) return
    this._createAndStartSource(this.startOffset)
    this.isPlaying = true
    this.state.updateDeck(this.id, { isPlaying: true })
  }

  pause() {
    if (!this.isPlaying) return
    this.startOffset = this.currentTime
    this._stopSource()
    this.isPlaying = false
    this.state.updateDeck(this.id, { isPlaying: false })
  }

  togglePlay() {
    this.isPlaying ? this.pause() : this.play()
  }

  stop() {
    this._stopSource()
    this.isPlaying = false
    this.startOffset = 0
    this.state.updateDeck(this.id, { isPlaying: false })
  }

  /** Jump to cue point without changing play state */
  jumpToCue() {
    const wasPlaying = this.isPlaying
    if (wasPlaying) this._stopSource()
    this.startOffset = this.cuePoint
    if (wasPlaying) this._createAndStartSource(this.startOffset)
  }

  /** Set cue at current position */
  setCue() {
    this.cuePoint = this.currentTime
  }

  /** Seek to specific time (click-to-seek from waveform) */
  seekTo(timeInSeconds) {
    const wasPlaying = this.isPlaying
    if (wasPlaying) this._stopSource()
    this.startOffset = Math.max(0, Math.min(timeInSeconds, this.duration || 0))
    if (wasPlaying) this._createAndStartSource(this.startOffset)
  }

  // ─── Hot Cues ─────────────────────────────────────────────────────────────

  setHotCue(index) {
    this.hotCues[index] = this.currentTime
    const updated = [...this.hotCues]
    this.state.updateDeck(this.id, { hotCues: updated })
  }

  jumpToHotCue(index) {
    if (this.hotCues[index] === null) return
    const wasPlaying = this.isPlaying
    if (wasPlaying) this._stopSource()
    this.startOffset = this.hotCues[index]
    if (wasPlaying) this._createAndStartSource(this.startOffset)
  }

  clearHotCue(index) {
    this.hotCues[index] = null
    this.state.updateDeck(this.id, { hotCues: [...this.hotCues] })
  }

  // ─── Loops ────────────────────────────────────────────────────────────────

  setLoopIn() {
    this.loopIn = this.currentTime
    this.loopActive = false
    if (this._source) {
      this._source.loop = false
    }
  }

  setLoopOut() {
    if (this.loopIn === null) return
    this.loopOut = this.currentTime
    if (this.loopOut <= this.loopIn) {
      this.loopOut = this.loopIn + 1
    }
    this._activateLoop()
  }

  toggleLoop() {
    if (!this.loopActive) {
      if (this.loopIn !== null && this.loopOut !== null) {
        this._activateLoop()
      }
    } else {
      this.loopActive = false
      if (this._source) this._source.loop = false
    }
  }

  halveLoop() {
    if (this.loopIn === null || this.loopOut === null) return
    const size = this.loopOut - this.loopIn
    this.loopOut = this.loopIn + Math.max(size / 2, 0.1)
    if (this._source && this.loopActive) this._source.loopEnd = this.loopOut
  }

  doubleLoop() {
    if (this.loopIn === null || this.loopOut === null) return
    const size = this.loopOut - this.loopIn
    this.loopOut = Math.min(this.loopIn + size * 2, this.duration)
    if (this._source && this.loopActive) this._source.loopEnd = this.loopOut
  }

  _activateLoop() {
    this.loopActive = true
    if (this._source) {
      this._source.loop      = true
      this._source.loopStart = this.loopIn
      this._source.loopEnd   = this.loopOut
    }
    // Jump into loop if playhead is outside
    const ct = this.currentTime
    if (ct < this.loopIn || ct > this.loopOut) {
      const wasPlaying = this.isPlaying
      if (wasPlaying) this._stopSource()
      this.startOffset = this.loopIn
      if (wasPlaying) this._createAndStartSource(this.startOffset)
    }
  }

  // ─── Tempo & Sync ─────────────────────────────────────────────────────────

  /** offset in ±percent (-8 to +8) */
  setTempoOffset(percent) {
    this.playbackRate = 1 + percent / 100
    this._isSynced = false
    if (this._source) this._source.playbackRate.value = this.playbackRate
    this.state.updateDeck(this.id, { isSynced: false })
  }

  /** Sync this deck's tempo to match the other deck's BPM */
  syncTo(otherDeck) {
    if (!otherDeck.bpm || !this.bpm) return
    this.playbackRate = otherDeck.bpm / this.bpm
    this._isSynced = true
    if (this._source) this._source.playbackRate.value = this.playbackRate
    this.state.updateDeck(this.id, { isSynced: true })
  }

  unsync() {
    this.playbackRate = 1.0
    this._isSynced = false
    if (this._source) this._source.playbackRate.value = 1.0
    this.state.updateDeck(this.id, { isSynced: false })
  }

  /** Tap tempo — call on each tap, averages last 8 intervals */
  tapTempo() {
    const now = performance.now()
    this._tapTimes.push(now)
    if (this._tapTimes.length > 8) this._tapTimes.shift()

    if (this._tapTimes.length >= 2) {
      const intervals = []
      for (let i = 1; i < this._tapTimes.length; i++) {
        intervals.push(this._tapTimes[i] - this._tapTimes[i - 1])
      }
      const avg = intervals.reduce((a, b) => a + b) / intervals.length
      this.bpm = Math.round(60000 / avg)
      this.bpm = Math.max(60, Math.min(200, this.bpm))
      this.state.updateDeck(this.id, { bpm: this.bpm })
    }
  }

  // ─── Key Lock ────────────────────────────────────────────────────────────

  toggleKeyLock() {
    this.keyLock = !this.keyLock
    if (this._source) {
      if ('preservesPitch' in this._source) this._source.preservesPitch = this.keyLock
      else if ('mozPreservesPitch' in this._source) this._source.mozPreservesPitch = this.keyLock
    }
    return this.keyLock
  }

  // ─── Beat Jump ──────────────────────────────────────────────────────────

  /** Jump forward/backward by a number of beats based on current BPM */
  beatJump(beats) {
    if (!this.bpm || !this.audioBuffer) return
    const secondsPerBeat = 60 / (this.bpm * this.playbackRate)
    const jumpTime = beats * secondsPerBeat
    const newTime = Math.max(0, Math.min(this.currentTime + jumpTime, this.duration))
    this.seekTo(newTime)
  }

  // ─── Load / Unload ────────────────────────────────────────────────────────

  loadBuffer(audioBuffer, bpm, trackName, artist) {
    this.stop()
    this.audioBuffer  = audioBuffer
    this.bpm          = bpm || 128
    this.startOffset  = 0
    this.cuePoint     = 0
    this.hotCues      = [null, null, null, null]
    this.loopIn       = null
    this.loopOut      = null
    this.loopActive   = false
    this._tapTimes    = []
    this._isSynced    = false
    this.playbackRate = 1.0

    this.state.updateDeck(this.id, {
      trackName,
      artist,
      bpm: this.bpm,
      duration: audioBuffer.duration,
      hotCues: [null, null, null, null],
      loopIn: null,
      loopOut: null,
      loopActive: false,
      isSynced: false,
    })
  }

  unload() {
    this.stop()
    this.audioBuffer = null
    this.state.updateDeck(this.id, {
      trackName: null,
      artist: null,
      bpm: 0,
      duration: 0,
      isPlaying: false,
    })
  }

  // ─── Private helpers ──────────────────────────────────────────────────────

  _createAndStartSource(offset = 0) {
    if (!this.audioBuffer) return
    const source = this.engine.ctx.createBufferSource()
    source.buffer       = this.audioBuffer
    source.playbackRate.value = this.playbackRate

    // Key lock: preserve pitch when changing tempo (essential for sync)
    if ('preservesPitch' in source) {
      source.preservesPitch = this.keyLock
    } else if ('mozPreservesPitch' in source) {
      source.mozPreservesPitch = this.keyLock  // Firefox fallback
    }

    if (this.loopActive && this.loopIn !== null && this.loopOut !== null) {
      source.loop      = true
      source.loopStart = this.loopIn
      source.loopEnd   = this.loopOut
    }

    // Connect into the engine's deck input gain
    source.connect(this.engine.deckNodes[this.id].input)
    source.start(0, offset)
    this.startContextTime = this.engine.ctx.currentTime
    this._source = source

    // When naturally ends, update state
    source.onended = () => {
      if (this._source === source) {
        this.isPlaying = false
        this._source   = null
        this.state.updateDeck(this.id, { isPlaying: false })
      }
    }
  }

  _stopSource() {
    if (this._source) {
      try { this._source.stop() } catch (_) {}
      this._source.onended = null
      this._source = null
    }
  }
}
